import * as yup from 'yup';
import { ChevronRight, Mail, Smartphone, UserPlus } from 'react-feather';
import { useEffect, useState } from 'react';
import { useForm } from 'react-form-state-manager';
import { useTranslation } from 'react-i18next';

import {
  OnCompletedLoginCallback, useAddUser, useLogin, useRequestLogin, useValidateUserEmail,
} from '../../common/Auth/useAuth';
import { emailRegex, getServerErrors } from '../../common/helpers';
import AtletaLogo from '../../common/Common/AtletaLogo';
import CompleteAuthenticatorLoginSessionMutation from './CompleteAuthenticatorLoginSessionMutation';
import UI from '../../common/UI';
import config from '../../config';
import useLocale from '../../common/useLocale';
import useMutation from '../../api/useMutation';
import useValidation from '../../common/useValidation';

const getRequestLoginSchema = (requireEmail: boolean) => yup.object().shape({
  email: (() => {
    let type = yup.string().ensure().matches(emailRegex, { excludeEmptyString: true, name: 'email' });

    if (requireEmail) {
      type = type.required();
    }

    return type;
  })(),
});

const loginSchema = yup.object().shape({
  pin: yup.string().required(),
});

const signupSchema = yup.object().shape({
  email: yup.string().required(),
  first_name: yup.string().required(),
  last_name: yup.string().required(),
});

const authenticatorSchema = yup.object().shape({
  code: yup.string().required(),
});

export interface LoginFormProps {
  onCompleted?: OnCompletedLoginCallback;
  title?: string;
  description?: string;
  scopedEmail?: string;
  scopedParticipantId?: string;
  loginRequestedForEmail?: string;
  allowSignup?: boolean;
}

const LoginForm = ({
  onCompleted, title, description, scopedEmail, scopedParticipantId = null, loginRequestedForEmail, allowSignup,
}: LoginFormProps) => {
  const [step, setStep] = useState<'request_login' | 'sign_up' | 'validate_login'>(
    loginRequestedForEmail ? 'validate_login' : 'request_login',
  );

  const [email, setEmail] = useState(loginRequestedForEmail ?? scopedEmail);

  const handleRequestLogin = (email: string | null, userExists: boolean) => {
    setEmail(email);

    if (userExists) {
      setStep('validate_login');
    } else if (allowSignup) {
      setStep('sign_up');
    }
  };

  const handleSignup = (email: string) => {
    setEmail(email);
    setStep('validate_login');
  };

  if (step === 'validate_login') {
    return (
      <ValidateLoginForm
        email={email}
        participantId={scopedParticipantId}
        onCompleted={onCompleted}
        onCancel={() => setStep('request_login')}
      />
    );
  }

  if (step === 'sign_up') {
    return (
      <SignupForm
        email={email}
        onSuccess={handleSignup}
        onCancel={() => setStep('request_login')}
      />
    );
  }

  return (
    <RequestLoginForm
      email={scopedEmail}
      participantId={scopedParticipantId}
      onSuccess={handleRequestLogin}
      title={title}
      description={description}
      allowSignup={allowSignup}
    />
  );
};

interface RequestLoginFormProps {
  email?: string | null;
  participantId?: string | null;
  onSuccess?: (email: string | null, userExists: boolean) => unknown;
  title?: string;
  description?: string;
  allowSignup?: boolean;
}

const RequestLoginForm = ({
  email, participantId, onSuccess, title, description, allowSignup,
}: RequestLoginFormProps) => {
  const { t } = useTranslation();

  const form = useForm({
    values: {
      email,
      participant_id: participantId,
    },
  });

  const [requestLogin, { error, loading }] = useRequestLogin({
    email: !form.values.participant_id ? form.values.email : null,
    participant_id: form.values.participant_id,
  }, {
    onCompleted: (email: string | null) => {
      onSuccess?.(email ?? form.values.email, !!email);
    },
  });

  const requireEmail = !form.values.participant_id;

  const { valid, errors } = useValidation({
    schema: getRequestLoginSchema(requireEmail),
    values: form.values,
    externalErrors: getServerErrors(error),
  });

  const { userExists, loading: validationLoading } = useValidateUserEmail({
    email: !form.values.participant_id ? form.values.email : null,
    participant_id: form.values.participant_id,
  }, {
    skip: !valid || allowSignup,
  });

  useEffect(() => {
    if (form.values.participant_id && !requireEmail && !userExists && !validationLoading) {
      form.set('participant_id', null);
    }
  }, [requireEmail, userExists, validationLoading, form]);

  return (
    <UI.Form onSubmit={() => requestLogin()}>
      <UI.FormGrid>
        <UI.FormGrid sc={{ columns: '30px 1fr', gutter: 0.5, alignVertical: 'center' }}>
          <UI.Badge sc={{ size: 30, brand: 'gray.100' }}>
            <UI.Icon sc={{ size: 1.5 }}>
              <AtletaLogo symbolOnly />
            </UI.Icon>
          </UI.Badge>
          <UI.H3>
            {title || (allowSignup ? t('log_in_or_sign_up') : t('log_in'))}
          </UI.H3>
        </UI.FormGrid>

        <UI.Div>
          {description || t('enter_email_to_continue')}
        </UI.Div>

        {requireEmail && (
          <UI.InputGroup sc={{ valid: !errors.email, touched: form.touched.email }}>
            <UI.InputLabel htmlFor="Email">
              {t('email_address')}
            </UI.InputLabel>
            <UI.InputWrapper>
              <UI.DebouncedInput
                {...form.text('email')}
                inputMode="email"
                id="Email"
                autoFocus
                placeholder={t('common:email_placeholder')}
                sc={{ size: 'lg' }}
              />
            </UI.InputWrapper>
            <UI.ErrorMessages attribute={t('email_address')} errors={errors.email} />
          </UI.InputGroup>
        )}

        {valid && requireEmail && !userExists && !validationLoading && !allowSignup && (
          <UI.Warning>
            {t('user_not_found')}
          </UI.Warning>
        )}

        <UI.Button
          type="submit"
          sc={{
            brand: 'primary', block: true, size: 'lg', loading, strong: true,
          }}
          disabled={!valid || (!userExists && !allowSignup) || loading}
        >
          {requireEmail ? (
            <>
              {t('continue')}
              {' '}
              <UI.Icon>
                <ChevronRight />
              </UI.Icon>
            </>
          ) : (
            <>
              <UI.Icon>
                <Mail />
              </UI.Icon>
              {' '}
              {t('send_login_code')}
            </>
          )}
        </UI.Button>
      </UI.FormGrid>
    </UI.Form>
  );
};

interface ValidateLoginFormProps {
  email: string;
  participantId: string | null;
  onCompleted?: OnCompletedLoginCallback,
  onCancel: () => void;
}

const ValidateLoginForm = ({ email, participantId, onCompleted, onCancel }: ValidateLoginFormProps) => {
  const { t } = useTranslation();

  const form = useForm({
    values: {
      pin: '',
    },
  });

  const [showAuthenticatorLoginForm, setShowAuthenticatorLoginForm] = useState(false);

  const [login, { data, error, loading }] = useLogin(
    {
      email: !participantId ? email : null,
      participant_id: participantId,
      pin: form.values.pin,
    },
    {
      onCompleted: (user, requireAuthenticatorCode) => {
        if (requireAuthenticatorCode) {
          setShowAuthenticatorLoginForm(true);
        } else {
          onCompleted?.(user, requireAuthenticatorCode);
        }
      },
    });

  const { errors } = useValidation({
    schema: loginSchema,
    values: form.values,
    externalErrors: getServerErrors(error),
  });

  useEffect(() => {
    if (errors.login) {
      // There is something wrong with the login request, make the user start over.
      onCancel();
    }
  }, [errors.login, onCancel]);

  const [showingLoginCodeHelp, setShowingLoginHelp] = useState(false);

  useEffect(() => {
    if (form.values.pin.trim().length === config.loginEmailCodeLength) {
      login();
    }
  }, [form.values.pin, login]);

  if (showAuthenticatorLoginForm) {
    return <AuthenticatorLoginForm onCompleted={onCompleted} onCancel={onCancel} />;
  }

  return (
    <UI.Form onSubmit={() => login()}>
      <UI.FormGrid>
        <UI.FormGrid sc={{ columns: '30px 1fr', gutter: 0.5, alignVertical: 'center' }}>
          <UI.Badge sc={{ size: 30, brand: 'gray.100' }}>
            <UI.Icon sc={{ size: 1.5 }}>
              <Mail />
            </UI.Icon>
          </UI.Badge>
          <UI.H3>
            {t('enter_your_login_code')}
          </UI.H3>
        </UI.FormGrid>

        <UI.Div>
          {t('login_requested', { email })}
          {' '}
          <UI.A onClick={() => setShowingLoginHelp((show) => !show)}>
            {t('did_not_receive_code')}
          </UI.A>
        </UI.Div>

        {showingLoginCodeHelp && (
          <UI.Info>
            {t('login_code_help')}
          </UI.Info>
        )}

        <UI.InputGroup sc={{ invalid: error && !!errors.pin }}>
          <UI.InputLabel htmlFor="Pin_0">
            {t('login_code')}
          </UI.InputLabel>
          <UI.PinCodeInput
            value={form.values.pin}
            onChange={(value) => form.set('pin', value)}
            length={config.loginEmailCodeLength}
            match={/[A-Z]/ig}
            name="pin"
            id="Pin"
            autoFocus
            autoComplete="one-time-code"
            placeholder="ABCDE"
            sc={{ size: 'lg' }}
          />
          <UI.ErrorMessages attribute={t('login_code')} errors={errors.pin} />
        </UI.InputGroup>

        <UI.Div>
          <UI.Button onClick={onCancel} sc={{ block: true, disabled: !!data || loading }}>
            {t('cancel')}
          </UI.Button>
        </UI.Div>
      </UI.FormGrid>
    </UI.Form>
  );
};

interface SignupFormProps {
  email?: string | null;
  onSuccess?: (email: string) => unknown;
  onCancel?: () => unknown;
  title?: string;
  description?: string;
}

const SignupForm = ({ email, onSuccess, onCancel }: SignupFormProps) => {
  const { t } = useTranslation();
  const { locale } = useLocale();

  const form = useForm({
    values: {
      email,
      first_name: '',
      last_name: '',
      locale,
    },
  });

  const [addUser, { error, loading }] = useAddUser(form.values, {
    onCompleted: (email: string) => {
      onSuccess?.(email);
    },
  });

  const { valid, errors } = useValidation({
    schema: signupSchema,
    values: form.values,
    externalErrors: getServerErrors(error),
  });

  return (
    <UI.Form onSubmit={() => addUser()}>
      <UI.FormGrid>
        <UI.FormGrid sc={{ columns: '30px 1fr', gutter: 0.5, alignVertical: 'center' }}>
          <UI.Badge sc={{ size: 30, brand: 'gray.100' }}>
            <UI.Icon sc={{ size: 1.5 }}>
              <UserPlus />
            </UI.Icon>
          </UI.Badge>
          <UI.H3>
            {t('create_an_account')}
          </UI.H3>
        </UI.FormGrid>

        <UI.Div>
          {t('create_an_account_description')}
        </UI.Div>

        <UI.InputGroup sc={{ valid: !errors.email, touched: form.touched.email }}>
          <UI.InputLabel htmlFor="Email">
            {t('email_address')}
          </UI.InputLabel>
          <UI.InputWrapper>
            <UI.DebouncedInput
              {...form.text('email')}
              inputMode="email"
              id="Email"
              placeholder={t('common:email_placeholder')}
              sc={{ size: 'lg' }}
            />
          </UI.InputWrapper>
          <UI.ErrorMessages attribute={t('email_address')} errors={errors.email} />
        </UI.InputGroup>

        <UI.InputGroup sc={{ valid: !errors.first_name, touched: form.touched.first_name }}>
          <UI.InputLabel htmlFor="SignupFirstName">
            {t('first_name')}
          </UI.InputLabel>
          <UI.DebouncedInput
            {...form.text('first_name')}
            sc={{ size: 'lg' }}
            id="SignupFirstName"
            autoFocus={!!form.initialValues.email}
          />
          <UI.ErrorMessages attribute={t('first_name')} errors={errors.email} />
        </UI.InputGroup>

        <UI.InputGroup sc={{ valid: !errors.last_name, touched: form.touched.last_name }}>
          <UI.InputLabel htmlFor="SignupLastName">
            {t('last_name')}
          </UI.InputLabel>
          <UI.DebouncedInput {...form.text('last_name')} sc={{ size: 'lg' }} id="SignupLastName" />
          <UI.ErrorMessages attribute={t('last_name')} errors={errors.email} />
        </UI.InputGroup>

        <UI.FormGrid sc={{ gutter: 0.5 }}>
          <UI.Button
            type="submit"
            sc={{
              brand: 'primary', block: true, size: 'lg', loading, strong: true,
            }}
            disabled={!valid || loading}
          >
            {t('create_account')}
          </UI.Button>

          {onCancel && (
            <UI.Button onClick={onCancel} sc={{ block: true, disabled: loading }}>
              {t('cancel')}
            </UI.Button>
          )}
        </UI.FormGrid>
      </UI.FormGrid>
    </UI.Form>
  );
};

interface AuthenticatorLoginFormProps {
  onCompleted?: OnCompletedLoginCallback,
  onCancel: () => void;
}

const AuthenticatorLoginForm = ({ onCompleted, onCancel }: AuthenticatorLoginFormProps) => {
  const { t } = useTranslation();

  const form = useForm({
    values: {
      code: '',
    },
  });

  const [login, { data, error, loading }] = useMutation(CompleteAuthenticatorLoginSessionMutation, {
    variables: {
      input: {
        code: form.values.code,
      },
    },
    onCompleted: (data) => onCompleted?.(data.completeAuthenticatorLoginSession.user, false),
    onError: () => {},
  });

  const { errors } = useValidation({
    schema: authenticatorSchema,
    values: form.values,
    externalErrors: getServerErrors(error),
  });

  useEffect(() => {
    if (errors.completeAuthenticatorLoginSession) {
      // There is something wrong with the login request, make the user start over.
      onCancel();
    }
  }, [errors.completeAuthenticatorLoginSession, onCancel]);

  useEffect(() => {
    if (form.values.code.trim().length === config.authenticatorCodeLength) {
      login();
    }
  }, [form.values.code, login]);

  return (
    <UI.Form onSubmit={() => login()}>
      <UI.FormGrid>
        <UI.FormGrid sc={{ columns: '30px 1fr', gutter: 0.5, alignVertical: 'center' }}>
          <UI.Badge sc={{ size: 30, brand: 'gray.100' }}>
            <UI.Icon sc={{ size: 1.5 }}>
              <Smartphone />
            </UI.Icon>
          </UI.Badge>
          <UI.H3>
            {t('open_your_authenticator_app')}
          </UI.H3>
        </UI.FormGrid>

        <UI.Div>
          {t('enter_authenticator_code_description')}
        </UI.Div>

        <UI.InputGroup sc={{ invalid: error && !!errors.code }}>
          <UI.InputLabel htmlFor="Pin_0">
            {t('authenticator_code')}
          </UI.InputLabel>
          <UI.PinCodeInput
            value={form.values.code}
            onChange={(value) => form.set('code', value)}
            spacingAfter={[2]}
            length={config.authenticatorCodeLength}
            name="code"
            id="Pin"
            autoFocus
            placeholder="123456"
            inputMode="numeric"
            sc={{ size: 'lg' }}
          />
          <UI.ErrorMessages attribute={t('common:authenticator.authenticator_code')} errors={errors.code} />
        </UI.InputGroup>

        <UI.Div>
          <UI.Button onClick={onCancel} sc={{ block: true, disabled: !!data || loading }}>
            {t('cancel')}
          </UI.Button>
        </UI.Div>
      </UI.FormGrid>
    </UI.Form>
  );
};

export default LoginForm;
